import { create } from 'zustand';
import { produce } from 'immer';

import {
  addressInfo,
  formattedPhoneNumber,
  getComments,
} from 'src/utils/common';
import { commonFormatterFunction } from '../utils/dataComparisonHelper';
import { fetchCaller, fetchCountryDetails } from 'src/store/apiStore';
import { fetchQuestionsAndSections } from './apiStore';

import { PersonRepoStoreType } from '../types/apiStoreType';
import { getSavedResponses } from '../utils/common';
import { formatDateForTable } from 'src/utils/datepickerHelper';
import { TextFormatType } from 'src/hooks/useTextTypeFormat';
import { hasUpdates } from '../utils/checkhasUpdated';

export const usePersonRepoStore = create<PersonRepoStoreType>((set, get) => ({
  personDetails: {},
  previousPersonDetails: {},
  question: undefined,
  isPersonInformationLoading: false,
  isQuestionInformationLoading: false,
  userMode: 'Edit',
  isSaveClicked: false,
  showPrimaryNumberError: false,
  showSecondaryNumberError: false,
  countryInfo: [],
  initialPersonData: {},
  isUpdateButtonDisabled: true,
  showTextValidationErr: false,
  showEmailValidation: false,
  showUrlValidation: false,
  callInfos: [],
  formValues: {
    PocPreviousData: {},
    Poc: {},
    Person: {},
  },
  textTypeFormat: [],
  qnts: undefined,
  currentQuestion: 0,
  lastUpdated: undefined,

  //searched person data binding
  updatePersonInformation: async (
    accountId: number | undefined,
    personId: number | undefined,
    entityId: string | undefined,
    token: string,
    tokenType: string,
  ) => {
    set(
      produce(state => {
        state.isPersonInformationLoading = true;
      }),
    );

    const countryCode = await fetchCountryDetails({ token, tokenType });
    set(
      produce(state => {
        state.countryInfo = countryCode?.Data;
      }),
    );

    if (accountId && personId && entityId) {
      const response = await fetchCaller({
        token,
        tokenType,
        accountId,
        personId,
        updatePersonLog: false,
      });
      const primaryPhone = response?.PrimaryPhone;
      const secondaryPhone = response?.SecondaryPhone;
      set(
        produce(state => {
          state.personDetails = {
            ...response,
            Birthdate: response.Birthdate ? new Date(response.Birthdate) : null,
            PrimaryPhone: {
              ...response?.PrimaryPhone,
              Number:
                primaryPhone?.CountryId === null
                  ? formattedPhoneNumber(primaryPhone?.Number, 1)
                  : primaryPhone?.Number,
              CountryId: primaryPhone?.CountryId ?? 1,
              Comments: getComments(
                primaryPhone,
                primaryPhone?.CountryId,
                get().countryInfo,
              ),
            },
            SecondaryPhone: {
              ...response?.SecondaryPhone,
              Number:
                response?.SecondaryPhone?.CountryId === null
                  ? formattedPhoneNumber(response?.SecondaryPhone?.Number, 1)
                  : response?.SecondaryPhone?.Number,
              CountryId: response?.SecondaryPhone?.CountryId ?? 1,
              Comments: getComments(
                secondaryPhone,
                secondaryPhone?.CountryId,
                get().countryInfo,
              ),
            },
          };
        }),
      );

      set(
        produce(state => {
          state.previousPersonDetails = get().personDetails;
        }),
      );

      const store = usePersonRepoStore.getState();
      set({ initialPersonData: commonFormatterFunction(store) });
    }
    set(
      produce(state => {
        state.isPersonInformationLoading = false;
      }),
    );
  },

  updateQuestionDetails: async (
    accountId: number | undefined,
    personId: number | undefined,
    entityId: string | undefined,
    token: string,
    tokenType: string,
  ) => {
    set(
      produce(state => {
        state.isQuestionInformationLoading = true;
      }),
    );

    if (accountId && personId && entityId) {
      const questions = await fetchQuestionsAndSections({
        token,
        tokenType,
        accountId,
        entityId,
      });

      set(
        produce(state => {
          state.question = questions; // callInfo

          const temp: any =
            questions?.Survey?.Sections &&
            questions?.Survey?.Sections.length > 0 &&
            questions?.Survey?.Sections.map((item: any) => {
              if (item.hasOwnProperty('Section')) {
                return {
                  ...item.Section,
                  Id: item?.Id,
                  $type:
                    'Proteus2.Api.Domain.Surveys.GlobalSectionReference, Proteus2.Api.Domain',
                  Order: item?.Order,
                };
              } else {
                return { ...item };
              }
            });

          const qnts = temp
            ?.map((item: any) => item.Questions)
            .flat()
            .filter((item: any) => item?.IsDeleted === false)
            .filter(
              (item: any) =>
                item.PersonAssociation === 1 || item.PersonAssociation === 0,
            );
          state.qnts = qnts;
        }),
      );

      if (get().question) {
        // response binding
        const initialResponses = getSavedResponses(get().qnts);

        set(
          produce(state => {
            state.formValues.PocPreviousData = {
              ...state.formValues.Poc,
              ...initialResponses,
            };
            state.formValues.Poc = {
              ...state.formValues.Poc,
              ...initialResponses,
            };
          }),
        );
      }
    }

    set(
      produce(state => {
        state.isQuestionInformationLoading = false;
      }),
    );
  },

  updatePersonDetails: (key: string, value: any, personDetailKey: string) => {
    set(
      produce(state => {
        if (personDetailKey !== '') {
          if (
            state.personDetails === undefined ||
            state.personDetails === null
          ) {
            state.personDetails = {
              ...state?.personDetails,
            };
            state.personDetails = {
              ...state?.personDetails,
              [`${personDetailKey}`]: {
                [key]: value,
              },
            };
          } else {
            state.personDetails[`${personDetailKey}`] = {
              ...state?.personDetails[`${personDetailKey}`],
              [key]: value,
            };
          }
        } else {
          state.personDetails = {
            ...state?.personDetails,
            [key]: value,
          };
        }
      }),
    );
  },

  //searched address data binding
  updateAddressDetails: (item: any) => {
    const address = addressInfo(item);
    set(
      produce(state => {
        if (state?.personDetails === undefined) {
          state.personDetails = {
            ...state?.personDetails,
          };
          state.personDetails = {
            ['PrimaryAddress']: {
              ...address,
            },
          };
        } else {
          state.personDetails.PrimaryAddress = {
            ...address,
          };
        }
      }),
    );
  },

  //set a selected person
  setSelectedPerson: (person: any) => {
    set(
      produce(state => {
        state.selectedPerson = { ...person };
      }),
    );
  },

  //change user mode
  setUserMode: (mode: string) => {
    set(
      produce(state => {
        state.userMode = mode;
      }),
    );
  },

  setIsSaveClicked: (mode: boolean) => {
    set(
      produce(state => {
        state.isSaveClicked = mode;
      }),
    );
  },

  setIsUpdatedButtonDis: (mode: boolean) => {
    set(
      produce(state => {
        state.isSaveClicked = mode;
      }),
    );
  },

  enableSaveBtn: (): boolean => {
    const firstName: string = get().personDetails?.FirstName?.trim();
    const lastName: string = get().personDetails?.LastName?.trim();
    return (
      firstName !== undefined &&
      firstName !== '' &&
      lastName !== undefined &&
      lastName !== ''
    );
  },

  setShowPhnValidation: (key: string, value: boolean) => {
    set({
      [key]: value,
    });
  },

  checkDataHasChanged: () => {
    const store = usePersonRepoStore.getState();
    const newData = commonFormatterFunction(store);

    if (JSON.stringify(newData) === JSON.stringify(get().initialPersonData)) {
      set({ isUpdateButtonDisabled: true });
      return true;
    } else {
      set({ isUpdateButtonDisabled: false });
      return false;
    }
  },

  checkPersonRepoDataHasChanged: () => {
    const store = usePersonRepoStore.getState();
    const formValues = usePersonRepoStore.getState().formValues.Poc;
    const previousFormValues =
      usePersonRepoStore.getState().formValues.PocPreviousData;
    // const newData = commonFormatterFunction(store);
    const isDiff = hasUpdates(previousFormValues, formValues);
    if (isDiff) {
      set({ isUpdateButtonDisabled: true });
      return false;
    } else {
      set({ isUpdateButtonDisabled: false });
      return true;
    }
  },

  resetStore: () => {
    set(
      produce(state => {
        return {
          ...state,
          personDetails: { ...state.previousPersonDetails },
          userMode: 'Edit',
          isSaveClicked: false,
          showPrimaryNumberError: false,
          showSecondaryNumberError: false,
        };
      }),
    );
  },

  //store function to reset the form values on cancel
  resetFormValues: () => {
    set(
      produce(state => {
        state.formValues.Poc = state.formValues.PocPreviousData;
      }),
    );
  },

  updateCallInfo: (obj: any) => {
    set(
      produce(state => {
        state.callInfos = [...state.callInfos, obj];
      }),
    );
  },

  deleteCallInfos: () => {
    set(
      produce(state => {
        state.callInfos = [];
      }),
    );
  },

  setTextTypeFormat: (value: Array<TextFormatType>) => {
    set(
      produce(state => {
        state.textTypeFormat = [...value];
      }),
    );
  },

  updateCurrentQuestion: (qnTempId: number) => {
    set(
      produce(state => {
        state.currentQuestion = qnTempId;
      }),
    );
  },

  updateDobComments: (val: any, qnTempId: number) => {
    getCurrentQnTempId(qnTempId, get().updateCurrentQuestion);
    set(
      produce(state => {
        if (state.formValues.Poc.DobComments === undefined) {
          state.formValues.Poc.DobComments = {
            [qnTempId]: {
              ...get().formValues.Poc.DobComments[qnTempId],
              ...val,
              birthdate: val.birthdateRaw,
            },
          };
        } else
          state.formValues.Poc.DobComments[qnTempId] = {
            ...get().formValues.Poc.DobComments[qnTempId],
            ...val,
            birthdate: val.birthdateRaw,
          };
      }),
    );
  },

  //store function to update the dob type questions in the Questions tab
  updateDob: (val: any, qnTempId: number) => {
    getCurrentQnTempId(qnTempId, get().updateCurrentQuestion);
    set(
      produce(state => {
        if (state.formValues.Poc.Dob === undefined) {
          state.formValues.Poc.Dob = {
            [qnTempId]: {
              ...get().formValues.Poc.Dob[qnTempId],
              ...val,
              birthdate: val.birthdateRaw,
            },
          };
        } else
          state.formValues.Poc.Dob[qnTempId] = {
            ...get().formValues.Poc.Dob[qnTempId],
            ...val,
            birthdate: val.birthdateRaw,
          };
      }),
    );
  },

  //store function to update the address details in the Questions tab
  updateAddressDetailsInQnTab: (val: any, qnTempId: number) => {
    getCurrentQnTempId(qnTempId, get().updateCurrentQuestion);
    set(
      produce(state => {
        if (state.formValues.Poc.Address === undefined) {
          state.formValues.Poc.Address = {
            [qnTempId]: {
              ...get().formValues.Poc.Address[qnTempId],
              ...val,
            },
          };
        } else {
          state.formValues.Poc.Address[qnTempId] = {
            ...get().formValues.Poc.Address[qnTempId],
            ...val,
          };
        }
      }),
    );
  },

  updateCalendar: (val: any, qnTempId: number) => {
    getCurrentQnTempId(qnTempId, get().updateCurrentQuestion);
    set(
      produce(state => {
        if (state.formValues.Poc.Date === undefined)
          state.formValues.Poc.Date = {
            [qnTempId]:
              val !== null && val !== undefined && val !== ''
                ? formatDateForTable(new Date(val), 'MM/dd/yyyy')
                : null,
          };
        else
          state.formValues.Poc.Date[qnTempId] =
            val !== null && val !== undefined && val !== ''
              ? formatDateForTable(new Date(val), 'MM/dd/yyyy')
              : null;
      }),
    );
  },

  updateChoice: (value: any, qnTempId: number) => {
    getCurrentQnTempId(qnTempId, get().updateCurrentQuestion);
    set(
      produce(state => {
        if (state.formValues.Poc.Choice === undefined)
          state.formValues.Poc.Choice = { [qnTempId]: [...value] };
        else state.formValues.Poc.Choice[qnTempId] = [...value];
      }),
    );
  },

  updateTextView: (val: any, qnTempId: number) => {
    getCurrentQnTempId(qnTempId, get().updateCurrentQuestion);
    set(
      produce(state => {
        if (state.formValues.Poc.TextView === undefined)
          state.formValues.Poc.TextView = { [qnTempId]: val };
        else state.formValues.Poc.TextView[qnTempId] = val;
      }),
    );
  },

  setShowTextValidationErr: (val: boolean) => {
    set(() => ({
      showTextValidationErr: val,
    }));
  },

  setShowEmailValidation: (val: boolean) => {
    set(() => ({
      showEmailValidation: val,
    }));
  },

  setlastUpdated: (val: any) => {
    set(() => ({
      lastUpdated: val,
    }));
  },

  setShowUrlValidation: (val: boolean) => {
    set(() => ({
      showUrlValidation: val,
    }));
  },
}));

function getCurrentQnTempId(qnTempId: number, updateCurrentQuestion: any) {
  updateCurrentQuestion(qnTempId);
}
